import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Box,
  Divider,
  FormControlLabel,
  MenuItem,
  Modal,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextareaAutosize,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { BulkPauseUnpauseModalProps } from '../../index';
import redux, { RootState } from '../../redux';
import CancelButton from './cancel-button';
import BulkPauseUnpauseSendButton from './save-button';
import Title from './title';

dayjs.extend(utc);
dayjs.extend(timezone);

const containerSx = {
  backgroundColor: '#FFFFFF',
  borderRadius: '8px',
  left: '50%',
  p: '20px',
  position: 'absolute',
  top: '50%',
  transform: 'translate(-50%, -50%)',
  width: 500,
};

const radioSx = () => ({
  '&.Mui-checked': {
    color: '#1C5BFE',
  },
  marginBottom: '-8px',
  marginTop: '-8px',
});

const labelSx = () => ({
  color: '#444C5B',
  fontFamily: 'Avenir Next',
  fontSize: 14,
  fontWeight: 500,
  lineHeight: '19.12px',
});

const selectSx = {
  '& .MuiSelect-outlined.MuiInputBase-input': {
    padding: '10.5px 7px',
  },
  marginLeft: '10px',
  padding: '0px 7px',
};

const BulkPauseUnpauseModal = (props: BulkPauseUnpauseModalProps) => {
  const { t } = useTranslation('bulkPauseUnpause');
  const { customer_group_id, customer_id } = props;
  const dispatch: typeof redux.store.dispatch = useDispatch();
  const modalOpen = useSelector((state: RootState) => state.modal.isOpen);
  const action = useSelector(
    (state: RootState) => state.bulkPauseUnpause.action,
  );
  const days = useSelector((state: RootState) => state.bulkPauseUnpause.days);
  const hours = useSelector((state: RootState) => state.bulkPauseUnpause.hours);
  const pauseOption = useSelector(
    (state: RootState) => state.bulkPauseUnpause.pauseOption,
  );
  const reason = useSelector(
    (state: RootState) => state.bulkPauseUnpause.reason,
  );
  const names = useSelector((state: RootState) => state.bulkPauseUnpause.names);

  dayjs.tz.setDefault(props.userTimezone);

  React.useEffect(() => {
    dispatch(
      redux.bulkPauseUnpause.setContext({ customer_group_id, customer_id }),
    );
  }, []);

  return (
    <Modal
      aria-describedby="modal-modal-description"
      aria-labelledby="modal-modal-title"
      data-testid="bulk-pause-unpause-modal"
      open={modalOpen}
    >
      <Box sx={containerSx}>
        <Stack direction="column" spacing="24px">
          <Title />
          <Stack
            alignContent="center"
            alignItems="center"
            direction="row"
            justifyContent="space-between"
          >
            <Typography
              sx={{
                color: '#0E3691',
                fontFamily: 'Avenir Next',
                fontSize: '14px',
                fontWeight: '600',
                letterSpacing: '2px',
                textTransform: 'uppercase',
              }}
            >
              {t('modal.selectAction')}
            </Typography>
            <Stack>
              <RadioGroup
                aria-labelledby="demo-radio-buttons-group-label"
                defaultValue="pause"
                name="radio-buttons-group"
                row
                sx={{
                  fontFamily: 'Avenir Next',
                  fontSize: '14px',
                  fontWeight: 500,
                  lineHeight: '18px',
                }}
                value={action}
              >
                <FormControlLabel
                  control={<Radio data-testid="radio" sx={radioSx} />}
                  label={
                    <Typography sx={labelSx}>{t('modal.pause')}</Typography>
                  }
                  onChange={() => {
                    dispatch(redux.bulkPauseUnpause.setAction('pause'));
                  }}
                  value="pause"
                />
                <FormControlLabel
                  control={<Radio data-testid="radio" sx={radioSx} />}
                  label={
                    <Typography sx={labelSx}>{t('modal.unpause')}</Typography>
                  }
                  onChange={() => {
                    dispatch(redux.bulkPauseUnpause.setAction('unpause'));
                  }}
                  sx={{
                    fontFamily: 'Avenir Next',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    lineHeight: '18px',
                  }}
                  value="unpause"
                />
              </RadioGroup>
            </Stack>
          </Stack>
          <Divider />
          {action === 'pause' && (
            <Stack>
              <RadioGroup
                aria-labelledby="demo-radio-buttons-group-label"
                defaultValue="pause"
                name="radio-buttons-group"
                row
                value={pauseOption}
              >
                <FormControlLabel
                  control={<Radio data-testid="radio" sx={radioSx} />}
                  label={
                    <Typography sx={labelSx}>{t('modal.pauseFor')}</Typography>
                  }
                  onChange={() => {
                    dispatch(redux.bulkPauseUnpause.setPauseOption('pauseFor'));
                  }}
                  value="pauseFor"
                />
                <Stack
                  alignContent="center"
                  alignItems="center"
                  direction="row"
                  justifyContent="start"
                >
                  <Select
                    IconComponent={ExpandMoreIcon}
                    onChange={(e) => {
                      dispatch(redux.bulkPauseUnpause.setDays(e.target.value));
                    }}
                    sx={selectSx}
                    value={days}
                  >
                    {[...Array(15)].map((_, index) => (
                      <MenuItem key={index + 'items'} value={index}>
                        {index}
                      </MenuItem>
                    ))}
                  </Select>
                  <Typography
                    sx={{
                      color: '#444C5B',
                      fontFamily: 'Avenir Next',
                      fontSize: '14px',
                      fontWeight: 500,
                      lineHeight: '18px',
                      marginLeft: '10px',
                    }}
                  >
                    {t('modal.days')}
                  </Typography>
                  <Select
                    IconComponent={ExpandMoreIcon}
                    onChange={(e) => {
                      dispatch(redux.bulkPauseUnpause.setHours(e.target.value));
                    }}
                    sx={selectSx}
                    value={hours}
                  >
                    {[...Array(24)].map((_, index) => (
                      <MenuItem key={index + 'items'} value={index}>
                        {index}
                      </MenuItem>
                    ))}
                  </Select>
                  <Typography
                    sx={{
                      color: '#444C5B',
                      fontFamily: 'Avenir Next',
                      fontSize: '14px',
                      fontWeight: 500,
                      lineHeight: '18px',
                      marginLeft: '10px',
                    }}
                  >
                    {t('modal.hours')}
                  </Typography>
                </Stack>
                <FormControlLabel
                  control={<Radio data-testid="radio" sx={radioSx} />}
                  label={
                    <Typography display="inline" sx={labelSx}>
                      {t('modal.pauseUntilManually')}
                    </Typography>
                  }
                  onChange={() => {
                    dispatch(
                      redux.bulkPauseUnpause.setPauseOption('manuallyPaused'),
                    );
                  }}
                  style={{
                    marginBottom: '15px',
                    marginTop: '15px',
                  }}
                  value="manuallyPaused"
                />
              </RadioGroup>
              <Divider />
            </Stack>
          )}
          {action === 'pause' && (
            <Stack>
              <Typography
                sx={{
                  color: '#444C5B',
                  fontFamily: 'Avenir Next',
                  fontSize: '14px',
                  fontWeight: '500',
                  lineHeight: '18px',
                  paddingBottom: '24px',
                }}
              >
                {pauseOption === 'pauseFor' &&
                  t('modal.alertForMachines', {
                    date: dayjs()
                      .add(days, 'day')
                      .add(hours, 'hour')
                      .tz()
                      .format('LLL')
                      .toString(),
                  })}
                {pauseOption === 'manuallyPaused' &&
                  t('modal.manually.unpaused')}
              </Typography>
              <Typography
                sx={{
                  fontFamily: 'Avenir Next',
                  fontSize: '13px',
                  fontWeight: 600,
                  letterSpacing: '0%',
                  lineHeight: '100%',
                  paddingBottom: '5px',
                  textTransform: 'capitalize',
                }}
              >
                {t('modal.selectedMachines')}
              </Typography>
              <Typography
                sx={{
                  color: '#444C5B',
                  fontFamily: 'Avenir Next',
                  fontSize: '13px',
                  fontWeight: '400',
                  letterSpacing: '0',
                  lineHeight: '17px',
                  paddingBottom: '24px',
                }}
              >
                {names.join(', ')}
              </Typography>

              <Typography
                sx={{
                  fontFamily: 'Avenir Next',
                  fontSize: '13px',
                  fontWeight: 600,
                  letterSpacing: '0',
                  lineHeight: '100%',
                  paddingBottom: '5px',
                  textTransform: 'capitalize',
                }}
              >
                {t('modal.notes')}
              </Typography>
              <TextareaAutosize
                aria-label="minimum height"
                minRows={3}
                onChange={(e) => {
                  dispatch(redux.bulkPauseUnpause.setReason(e.target.value));
                }}
                placeholder="Write note.."
                style={{
                  backgroundColor: '#FFF',
                  border: '1px solid #DCDEE2',
                  borderRadius: '6px',
                  color: '#444C5B',
                  padding: '10px',
                }}
                value={reason}
              />
              <Divider />
            </Stack>
          )}
          {action === 'unpause' && (
            <Stack>
              <Typography
                sx={{
                  color: '#444C5B',
                  fontFamily: 'Avenir Next',
                  fontSize: '14px',
                  fontWeight: '500',
                  lineHeight: '17px',
                  paddingBottom: '24px',
                }}
              >
                {t('modal.alertResume')}
              </Typography>
              <Typography
                sx={{
                  fontFamily: 'Avenir Next',
                  fontSize: '13px',
                  fontWeight: 600,
                  letterSpacing: '0%',
                  lineHeight: '100%',
                  paddingBottom: '5px',
                  textTransform: 'capitalize',
                }}
              >
                {t('modal.selectedMachines')}
              </Typography>
              <Typography
                sx={{
                  fontFamily: 'Avenir Next',
                  fontSize: '13px',
                  fontWeight: '400',
                  lineHeight: '18px',
                  paddingBottom: '25px',
                }}
              >
                {names.join(', ')}
              </Typography>
              <Divider />
            </Stack>
          )}
          <Stack direction="row" justifyContent="space-between">
            <CancelButton />
            <BulkPauseUnpauseSendButton />
          </Stack>
        </Stack>
      </Box>
    </Modal>
  );
};

export default BulkPauseUnpauseModal;
